import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import urls from 'config/urls.json';
import { toast } from 'react-toastify';
import { get, patch, post } from 'utils/axios';

export const sendMessage = createAsyncThunk('SEND_MESSAGE', async (data, thunkAPI) => {
  const response = await post(urls.SEND_MESSAGE, data, thunkAPI);
  return response.data;
});

export const getCharacterChats = createAsyncThunk('GET_CHATS', async (data, thunkAPI) => {
  const response = await get(`${urls.GET_CHATS}/${data.slug}`, data.filters, thunkAPI);
  return response.data;
});

export const getConversions = createAsyncThunk('GET_CONVERSIONS', async (data, thunkAPI) => {
  const response = await get(`${urls.GET_CONVERSIONS}`, data, thunkAPI);
  return response.data;
});

const initialState = {
  isConversionsLoading: false,
  conversions: [],
  hasMoreConversions: true,
  isLoading: false,
  chats: [],
  isSending: false,
  isSent: false,
  hasMore: true,
  isConversionsRefresh: false,
  selectedConversionSlug: ''
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    reset: () => initialState,
    resetChats: (state) => {
      state.chats = initialState.chats;
    },
    resetSent: (state) => {
      state.isSent = initialState.isSent;
    },
    incomingMessage: (state, { payload }) => {
      if (payload) {
        const findChat = state.chats.findIndex((x) => x._id === payload._id);
        if (findChat > -1) {
          state.chats[findChat] = { ...state.chats[findChat], ...payload };
        } else {
          state.chats = [payload, ...state.chats];
        }
        const foundIndex = state.conversions.findIndex((x) => x.characterDetails._id === payload.character);
        if (foundIndex > -1) {
          state.conversions[foundIndex].lastMessage = payload;
        } else {
          state.isConversionsRefresh = true;
        }
      }
    },
    setSelectedConversationSlug: (state, { payload }) => {
      state.selectedConversionSlug = payload;
    },
    sortConversations: (state) => {
      state.conversions = state.conversions.sort((a, b) => new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt));
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.isSending = true;
        state.isSent = false;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        const { success, data } = action.payload;
        if (success) {
          state.chats = [data, ...state.chats];
          const foundIndex = state.conversions.findIndex((x) => x.characterDetails._id === data.character);
          if (foundIndex > -1) {
            state.conversions[foundIndex].lastMessage = data;
          } else {
            state.isConversionsRefresh = true;
          }
          state.isSent = true;
        } else {
          toast.error('Something went wrong!', 3000);
        }
        state.isSending = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        console.log(action.payload);
        toast.error(action.payload?.message || 'Something went wrong', 3000);
        if (action.payload?.data) {
          state.chats = [...state.chats, action.payload?.data];
          state.isSent = true;
        }
        state.isSending = false;
      });

    builder
      .addCase(getCharacterChats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCharacterChats.fulfilled, (state, action) => {
        const { success, chats, page, hasMore } = action.payload;
        state.hasMore = hasMore;
        if (success) {
          state.chats = page === 1 ? chats : [...state.chats, ...chats];
        } else {
          toast.error('Something went wrong!', 3000);
        }
        state.isLoading = false;
      })
      .addCase(getCharacterChats.rejected, (state, action) => {
        toast.error(action.payload?.message || 'Something went wrong', 3000);
        state.isLoading = false;
      });

    builder
      .addCase(getConversions.pending, (state) => {
        state.isConversionsLoading = true;
      })
      .addCase(getConversions.fulfilled, (state, action) => {
        const { success, conversions, hasMore, page } = action.payload;
        if (success) {
          state.conversions = page === 1 ? conversions : [...state.conversions, ...conversions];
          state.hasMoreConversions = hasMore;
        } else {
          toast.error('Something went wrong!', 3000);
        }
        state.isConversionsLoading = false;
        state.isConversionsRefresh = false;
      })
      .addCase(getConversions.rejected, (state, action) => {
        toast.error(action.payload?.message || 'Something went wrong', 3000);
        state.isConversionsLoading = false;
        state.hasMoreConversions = false;
      });
  }
});

export const { reset, resetSent, resetChats, incomingMessage, setSelectedConversationSlug, sortConversations } = chatSlice.actions;

export default chatSlice.reducer;
